import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActionArea,
  LinearProgress,
  Skeleton,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { toast } from "react-toastify";
import { targetsApi } from "../../Components/targetsApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { useListViewMode } from "../../hooks/useListPreferences";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { GlobalSearchBar } from "../../Components/ERPList";
import { RoleContext } from "../../Contexts/RoleContext";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === "dark" ? undefined : "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const LIST_ID = "targets";

function TargetsPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [archivedFilter, setArchivedFilter] = useState("false");
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [archiveLoading, setArchiveLoading] = useState(null);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await targetsApi.list({
        search: debouncedSearch.trim() || undefined,
        archived: archivedFilter === "all" ? undefined : archivedFilter,
      });
      setTargets(data.targets || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load targets");
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, archivedFilter]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleArchive = async (e, t) => {
    e.stopPropagation();
    setArchiveLoading(t.id);
    try {
      await targetsApi.archive(t.id);
      toast.success(`"${t.name}" archived.`);
      fetchTargets();
    } catch (err) {
      toast.error(err?.message || "Failed to archive");
    } finally {
      setArchiveLoading(null);
    }
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setArchivedFilter("false");
    toast.info("Filters cleared.");
  };

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  };

  const formatProgress = (t) => {
    if (t.metric === "conversion_rate") return `${Number(t.current_progress).toFixed(1)}%`;
    if (t.metric === "revenue") return Number(t.current_progress).toLocaleString();
    return String(Math.round(t.current_progress));
  };

  const formatGoal = (t) => {
    if (t.metric === "conversion_rate") return `${Number(t.goal).toFixed(0)}%`;
    if (t.metric === "revenue") return Number(t.goal).toLocaleString();
    return String(Math.round(t.goal));
  };

  const canManage = role === "admin" || role === "Manager";

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Employee Targets
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Assign and track individual employee targets.
                    </Typography>
                  </Box>
                  {canManage && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/targets/create")}
                    >
                      Create Target
                    </Button>
                  )}
                </Box>
              </Box>

              <Box className={styles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search name, description…"
                />
                <Box className={styles.headerActions}>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    variant="outlined"
                    size="small"
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Tooltip title={viewMode === "cards" ? "Table view" : "Card view"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {filterOpen && (
                <Fade in={filterOpen}>
                  <FilterPaper variant="outlined">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="archived"
                            value={archivedFilter}
                            label="Status"
                            onChange={(e) => setArchivedFilter(e.target.value)}
                          >
                            <MenuItem value="false">Active</MenuItem>
                            <MenuItem value="true">Archived</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} className={styles.filterActions}>
                        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </FilterPaper>
                </Fade>
              )}

              {loading && (
                <Box className={styles.loadingContainer}>
                  {viewMode === "cards" ? (
                    <Grid container spacing={2}>
                      {[...Array(6)].map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box>
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                      <Skeleton variant="rectangular" height={400} />
                    </Box>
                  )}
                </Box>
              )}

              {!loading && targets.length === 0 && (
                <Box className={styles.emptyState}>
                  <TrackChangesIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No targets found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting filters or create a new target.
                  </Typography>
                  {canManage && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/targets/create")}
                      sx={{ mt: 2 }}
                    >
                      Create Target
                    </Button>
                  )}
                </Box>
              )}

              {!loading && targets.length > 0 && viewMode === "cards" && (
                <Grid container spacing={2} className={styles.cardsGrid}>
                  {targets.map((t) => (
                    <Grid item xs={12} sm={6} md={4} key={t.id}>
                      <StyledCard>
                        <CardActionArea onClick={() => navigate(`/targets/${t.id}`)}>
                          <CardHeader
                            avatar={
                              <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 1, borderRadius: 2 }}>
                                <TrackChangesIcon />
                              </Box>
                            }
                            title={t.name}
                            subheader={`${t.metric_label} · ${t.scope_display ?? "Overall"} · ${(t.employee_names || []).join(", ") || "—"} · ${t.timeframe}`}
                            action={t.archived ? (
                              <Chip label="Archived" color="default" size="small" variant="outlined" />
                            ) : null}
                            className={styles.cardHeader}
                          />
                          <CardContent className={styles.cardContent}>
                            <Typography variant="body2" color="text.secondary">
                              Scope: {t.scope_display ?? "Overall"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Goal: {formatGoal(t)}
                            </Typography>
                            <Typography variant="body2">
                              Progress: {formatProgress(t)} / {formatGoal(t)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, (Number(t.current_progress) / Number(t.goal)) * 100)}
                              sx={{ mt: 1, mb: 1, borderRadius: 1, height: 8 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Reward:{" "}
                              {t.reward_type === "cash" && t.reward_value != null
                                ? `Cash ${Number(t.reward_value).toLocaleString()}`
                                : t.reward_description || t.reward_type}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/targets/${t.id}`);
                                }}
                              >
                                View
                              </Button>
                              {canManage && !t.archived && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/targets/${t.id}/edit`);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ArchiveIcon />}
                                    disabled={archiveLoading === t.id}
                                    onClick={(e) => handleArchive(e, t)}
                                  >
                                    {archiveLoading === t.id ? "…" : "Archive"}
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              )}

              {!loading && targets.length > 0 && viewMode === "table" && (
                <TableContainer component={Paper} variant="outlined" className={styles.tableContainer}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Target Name</TableCell>
                        <TableCell>Assigned Employee(s)</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Metric</TableCell>
                        <TableCell>Goal</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Timeframe</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {targets.map((t) => (
                        <TableRow
                          key={t.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/targets/${t.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {t.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{(t.employee_names || []).join(", ") || "—"}</TableCell>
                          <TableCell>{t.scope_display ?? "Overall"}</TableCell>
                          <TableCell>{t.metric_label}</TableCell>
                          <TableCell>{formatGoal(t)}</TableCell>
                          <TableCell>
                            {formatProgress(t)} / {formatGoal(t)}
                          </TableCell>
                          <TableCell>{t.timeframe}</TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" startIcon={<VisibilityIcon />} onClick={() => navigate(`/targets/${t.id}`)}>
                                View
                              </Button>
                              {canManage && !t.archived && (
                                <>
                                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/targets/${t.id}/edit`)}>
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ArchiveIcon />}
                                    disabled={archiveLoading === t.id}
                                    onClick={(e) => handleArchive(e, t)}
                                  >
                                    Archive
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default TargetsPage;
